import { Icon } from '@studio/design-system/icon';
import { useDocumentActive, useInterval, useUpdateEffect } from '@studio/react-utils/hooks';
import { toArray, toObjectEntries } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { dequal } from 'dequal';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { twMerge } from 'cn';
import type { LayoutProps } from './types.js';
import { Lang } from '#~/command/index.js';
import { useProfile } from '#~/gamepad/hooks.js';
import { evaluate, getGamepad, Manifest } from '#~/gamepad/index.js';
import type { Action } from '#~/gamepad/index.js';

/** `<Layout>` component. */
export const Layout = ({ className, gamepadId, style, ...props }: LayoutProps): ReactElement => {
  const [profile] = useProfile(gamepadId);
  const [actions, setActions] = useState<T_Maybe<Set<Action>>>(undefined);

  const commands = useMemo(
    () =>
      toObjectEntries(Manifest)
        .sort((a, b) => a[1].rank - b[1].rank)
        .map(([command]) => command),
    []
  );

  const isActive = useDocumentActive();

  const interval = useInterval(
    () => {
      const gamepad = getGamepad(gamepadId);

      if (!gamepad) {
        setActions(undefined);

        return;
      }

      const current = new Set(evaluate(gamepad, profile.formula)?.map((µ) => µ.command));

      if (!dequal(current, actions)) {
        setActions(current);
      }
    },
    { period: 250, start: isActive }
  );

  useUpdateEffect(() => {
    interval[isActive ? 'start' : 'stop']();

    if (!isActive) {
      setActions(undefined);
    }
  }, [isActive]);

  return (
    <div
      className={twMerge(
        'grid grid-flow-col grid-cols-3 justify-between gap-x-40 border-t border-base-100',
        className
      )}
      style={{ ...style, gridTemplateRows: `repeat(${Math.ceil(commands.length / 3)},1fr)` }}
      {...props}
    >
      {commands.map((command) => {
        const { icon, title } = Manifest[command];

        return (
          <div
            className={twMerge(
              'flex h-[55px] items-center gap-8 border-b border-inherit pl-16 text-muted',
              actions?.has(command) && 'bg-base-50 text-foreground',
              className
            )}
            key={command}
          >
            <div className='text-xl'>
              {toArray(icon).map((µ, index) => (
                <>
                  {index > 0 && <span className='mx-8'>+</span>}
                  <Icon icon={µ} title={title} />
                </>
              ))}
            </div>
            <div>{Lang[command]}</div>
          </div>
        );
      })}
    </div>
  );
};
